import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Divider,
  Theme,
  Typography,
  Stack,
  Box,
  IconButton,
} from "@mui/material";
import {
  MailOutline,
  Clear,
  Group,
  FileDownloadOutlined,
} from "@mui/icons-material";
import Fuse from "fuse.js";
import { map, sortBy, debounce } from "lodash";
import {
  useCourseDetails,
  uploadManualDocument,
  sendParticipantsReminders,
  sendCustomReminders,
  forceDocumentValidation,
  sendInternalMassReminder,
  getParticipantMergedFileBase64,
} from "api";
import {
  FeedbackContext,
  DocumentDialogContext,
  MessageContext,
  AppContext,
} from "contexts";
import {
  useAvailableDocumentActions,
  useAvailableSpecialDocumentActions,
  useStyles,
  useCanInteractWithParticipantDocument,
} from "hooks";
import {
  CSSGenerator,
  DocumentParticipantInfo,
  Permission,
  DocumentType,
  EmailSubject,
  DocumentStatus,
  EmailForm,
  DocumentShortNames,
  DocumentCompanyStatus,
  AppRoutes,
  DocumentParticipantInfoData,
} from "interfaces";
import {
  CustomTable,
  TableCellType,
  AccordionInfo,
  SearchBar,
  TableColumnData,
  CustomButton,
  MassiveReminderDialog,
  Restricted,
  CellIconData,
  DownloadButton,
} from "components";
import {
  parseCourseParticipantsData,
  getDocumentIconButtonData,
  getUploadDialogTitle,
  getValidationDialogTitle,
  getValidationDialogMinorAuthTitle,
  getValidationDialogVulnerabilityTitle,
  DocumentActions,
  getMessageDialogParams,
  formatCourseRangeDates,
  ParsedCourseDetailsCSVInfo,
  parseCourseDetailsInfoToCSV,
  parseMultipleEmails,
  buildParticipantFileName,
  downloadBase64File,
} from "utils";

type Props = {
  columnDefs: TableColumnData[];
  documentType: DocumentType;
  sencenet: string;
  route: AppRoutes;
};

const DEBOUNCE_TIME = 600; // ms

export const CourseDetailsPage: React.FC<Props> = ({
  columnDefs,
  documentType,
  sencenet,
  route,
}) => {
  /* CONTEXTS */
  const { isAllowedTo, user } = useContext(AppContext);
  const { toggleLoader, showSnackbar } = useContext(FeedbackContext);
  const { openMessageDialog, openInformationDialog } =
    useContext(MessageContext);
  const {
    openValidationDialog,
    openUploadDialog,
    closeValidationDialog,
    closeUploadDialog,
  } = useContext(DocumentDialogContext);

  /* HOOKS */
  const styles = useStyles(generateStyles);
  const { course, loading } = useCourseDetails({
    sencenet,
    documentType,
  });
  const getDocumentActions = useAvailableDocumentActions();
  const getSpecialDocumentActions = useAvailableSpecialDocumentActions();
  const history = useHistory();
  const canInteractDocument = useCanInteractWithParticipantDocument();

  /* STATES AND MEMOS */
  const [massiveReminderDialogOpen, setMassiveReminderDialogOpen] =
    useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  const participantsData = useMemo(() => {
    if (course?.documentParticipantInfo) {

      const revisedParticipantData = course?.documentParticipantInfo.map((p) => {
        if(p.minorAuthorizationStatus === null){
          return {
            ...p,
            minorAuthorizationStatus: DocumentStatus.INACTIVE
          }}
        return p
        })

        const parsedRevisedData = revisedParticipantData.map((p) => {
          if(p.vulnerabilityStatus === null){
            return {
              ...p,
              vulnerabilityStatus: DocumentStatus.INACTIVE
            }}
          return p
        });

        
      return parseCourseParticipantsData(
        parsedRevisedData,
        history,
      );
    }
    return [];
  }, [course?.documentParticipantInfo, history]);

  const [participantsTableRawData, setParticipantsTableRawData] =
    useState<DocumentParticipantInfoData[]>(participantsData);
  const pageName = useMemo(() => `[${documentType}Page]`, [documentType]);

  const fuse = useMemo(() => {
    const data = participantsData.map((p) => ({
      name: p.name,
      rut: p.rut,
      email: p.email,
    }));
    const index = Fuse.createIndex(["name", "rut", "email"], participantsData);
    return new Fuse(
      data,
      {
        threshold: 0.4,
        isCaseSensitive: false,
        keys: ["name", "rut", "email"],
      },
      index,
    );
  }, [participantsData]);

  // Variable to determine if massive reminder should be sent to responsable
  const massiveReminderToResponsable: boolean = useMemo(
    () => isAllowedTo(Permission.CAN_SEND_RESPONSABLE_MAIL),
    [isAllowedTo],
  );

  const pendingManagementParticipants = useMemo(
    () =>
      sortBy(
        course?.documentParticipantInfo.filter((dp) =>
          massiveReminderToResponsable
            ? [
                DocumentStatus.PENDING,
                DocumentStatus.CORRECTION_REQUESTED,
              ].includes(dp.mainSignerStatus)
            : !dp.participant.hasMissingData &&
              dp.mainSignerStatus === DocumentStatus.PENDING,
        ),
        "mainSignerStatus",
      ),
    [course?.documentParticipantInfo, massiveReminderToResponsable],
  );

  const dialogInfoData = useMemo(
    () => ({
      courseName: course?.name ?? "",
      sencenet: course?.sencenet ?? "",
      sence: course?.senceCode ?? "",
      dates: formatCourseRangeDates(course),
      participantsNumber:
        course?.documentParticipantInfo?.length.toString() ?? "",
    }),
    [course],
  );

  const disableReminderButtonsByCompanyStatus = useMemo(
    () =>
      documentType === DocumentType.PRECONTRACT &&
      course?.companySignatureStatus !== DocumentCompanyStatus.VALIDATED,
    [course?.companySignatureStatus, documentType],
  );

  const disableMassReminderButton = useMemo(
    () => pendingManagementParticipants.length === 0,
    [pendingManagementParticipants.length],
  );

  const csvHeaders = useMemo(() => {
    const headers: {
      label: string;
      key: keyof ParsedCourseDetailsCSVInfo;
    }[] = [
      { label: "Nombre", key: "name" },
      { label: "RUT", key: "rut" },
      { label: "Correo electrónico", key: "email" },
      { label: "Teléfono", key: "phone" },
      { label: "Firma participante", key: "mainSignerStatus" },
    ];
    if (documentType === DocumentType.PRECONTRACT) {
      headers.push({ label: "Firma empresa", key: "secondarySignerStatus" });
    }
    return headers;
  }, [documentType]);

  const parsedExportableData: ParsedCourseDetailsCSVInfo[] = useMemo(() => {
    if (!course?.documentParticipantInfo) return [];
    return parseCourseDetailsInfoToCSV(course?.documentParticipantInfo);
  }, [course?.documentParticipantInfo]);

  /* CALLBACKS */
  /* PAGE CALLBACKS */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedParticipantsFilter = useCallback(
    debounce((text: string) => {
      if (text.length <= 2) {
        setParticipantsTableRawData(participantsData);
      }
      const fuseResult = map(fuse.search(text), "item");
      setParticipantsTableRawData(
        participantsData.filter((p) => map(fuseResult, "rut").includes(p.rut)),
      );
    }, DEBOUNCE_TIME),
    [setParticipantsTableRawData, participantsData, fuse],
  );

  const onClearIconClick = useCallback(() => {
    setSearchText("");
    setParticipantsTableRawData(participantsData);
  }, [setSearchText, setParticipantsTableRawData, participantsData]);

  const searchBarOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      setSearchText(text);
      if (text.length <= 2) {
        setParticipantsTableRawData(participantsData);
        return;
      }
      debouncedParticipantsFilter(event.target.value);
    },
    [debouncedParticipantsFilter, setSearchText, participantsData],
  );

  /* REMINDER DIALOG CALLBACKS */
  const onOpenMassiveReminderDialog = useCallback(() => {
    setMassiveReminderDialogOpen(true);
  }, [setMassiveReminderDialogOpen]);

  const onCloseMassiveReminderDialog = useCallback(() => {
    setMassiveReminderDialogOpen(false);
  }, [setMassiveReminderDialogOpen]);

  const sendMassiveReminder = useCallback(
    async (email: string | null) => {
      if (massiveReminderToResponsable && !email) return;
      const documentIds = map(pendingManagementParticipants, "id");
      const participants = map(pendingManagementParticipants, (participant) => {
        return {
          name: participant.participant.name,
          rut: participant.participant.rut,
          email: participant.participant.email,
          documentStatus: participant.document.status,
          comment:
            participant.document.status === DocumentStatus.CORRECTION_REQUESTED
              ? participant.lastCorrectionRequestComment
              : "No ha firmado el documento",
        };
      });

      toggleLoader(true, `${pageName} Sending massive reminder`);
      const massiveReminderSuccesful = massiveReminderToResponsable
        ? await sendInternalMassReminder(
            {
              emails: [email ?? ""],
              sencenet,
              documentType,
              participants,
            },
            { sencenet, documentType },
          )
        : await sendParticipantsReminders(
            { documentIds, documentType },
            { sencenet, documentType },
          );
      toggleLoader(false, `${pageName} Sending massive reminder`);
      if (!massiveReminderSuccesful) {
        showSnackbar("Algo falló al enviar el recordatorio masivo", "error");
        return;
      }
      setMassiveReminderDialogOpen(false);
      showSnackbar("Recordatorio masivo enviado exitosamente", "success");
    },
    [
      pendingManagementParticipants,
      toggleLoader,
      pageName,
      massiveReminderToResponsable,
      documentType,
      sencenet,
      showSnackbar,
    ],
  );

  const onReminderEditionFinished = useCallback(
    async (id: number, emailData: EmailForm) => {
      toggleLoader(true, `${pageName} Sending individual reminder`);
      try {
        const emails = parseMultipleEmails(emailData.email);

        const individualReminderSuccesful = await sendCustomReminders(
          {
            emailsData: [
              {
                message: emailData.message,
                emails,
                subject: emailData.subjectOption ?? EmailSubject.REQUEST_SIGN,
                documentId: id,
              },
            ],
            documentType,
          },
          { sencenet, documentType },
        );

        if (!individualReminderSuccesful) {
          throw new Error("mutation failed");
        }
        toggleLoader(false, `${pageName} Sending individual reminder`);
        showSnackbar("Recordatorio individual enviado exitosamente", "success");
      } catch (e) {
        console.log(e)
        toggleLoader(false, `${pageName} Sending individual reminder`);
        showSnackbar(
          "Algo falló al enviar el recordatorio individual",
          "error",
        );
        console.error(e);
      }
    },
    [toggleLoader, pageName, documentType, sencenet, showSnackbar],
  );

  const onReminderClick = useCallback(
    (id: number) => {
      const docParticipantData = course?.documentParticipantInfo.find(
        (p) => p.id === id,
      );

      if (!docParticipantData) return;

      const { canSendReminderToResponsable, canSendParticipantReminder } =
        getDocumentActions(docParticipantData.document);

      if (canSendReminderToResponsable) {
        openMessageDialog(
          getMessageDialogParams(
            course,
            docParticipantData,
            DocumentActions.SEND_REMINDER_TO_RESPONSABLE,
            documentType,
            route,
            onReminderEditionFinished,
            user,
          ),
        );
        return;
      }

      if (canSendParticipantReminder) {
        openMessageDialog(
          getMessageDialogParams(
            course,
            docParticipantData,
            DocumentActions.SEND_REMINDER_TO_SIGNER,
            documentType,
            route,
            onReminderEditionFinished,
            user,
          ),
        );
        return;
      }
    },
    [
      course,
      route,
      getDocumentActions,
      openMessageDialog,
      user,
      documentType,
      onReminderEditionFinished,
    ],
  );

  const getReminderButtonIconData = useCallback(
    (id: number): CellIconData | null => {
      const docParticipantData = course?.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      if (!docParticipantData) return null;
      const { canSendParticipantReminder, canSendReminderToResponsable } =
        getDocumentActions(docParticipantData.document);

      const { hasMissingData } = docParticipantData.participant;
      const pendingCompanySignature = [
        DocumentCompanyStatus.PENDING,
        DocumentCompanyStatus.PRESIGNED,
      ].includes(docParticipantData.document.companyStatus);

      const disabled = hasMissingData || pendingCompanySignature;

      if (canSendReminderToResponsable) {
        return {
          icon: <MailOutline />,
          tooltipLabel: "Enviar recordatorio al PDC",
        };
      }

      if (canSendParticipantReminder) {
        return {
          icon: <MailOutline />,
          tooltipLabel:
            hasMissingData && pendingCompanySignature
              ? "Este participante no puede firmar digitalmente debido a que la empresa aún no firma y además no posee todos los datos necesarios"
              : pendingCompanySignature
              ? "Este participante no puede firmar digitalmente debido a que la empresa aún no firma"
              : hasMissingData
              ? "Este participante no puede firmar digitalmente debido a que no posee todos los datos necesarios"
              : "Enviar recordatorio al participante",
          disabled,
        };
      }
      return null;
    },
    [course, getDocumentActions],
  );

  /* FILE DIALOG CALLBACKS (VALIDATION, FORCED VALIDATION, UPLOAD) */
  const onCloseValidationDialog = useCallback(() => {
    closeValidationDialog();
  }, [closeValidationDialog]);

  const onCloseUploadDialog = useCallback(() => {
    closeUploadDialog();
  }, [closeUploadDialog]);

  const manuallyUploadFile = useCallback(
    async (
      file: File,
      documentParticipantInfo: DocumentParticipantInfo | null,
      callback: () => void,
    ) => {
      if (!documentParticipantInfo) return;
      toggleLoader(true, `${pageName} Uploading file`);
      const { id } = documentParticipantInfo;
      // Send file
      try{
        const wasUploaded = await uploadManualDocument(
          {
            documentId: id,
            file,
          },
          {
            sencenet,
            documentType,
          },
          documentType,
        );
        if (!wasUploaded) {
          toggleLoader(false, `${pageName} Uploading file`);
          showSnackbar("Algo falló al subir el documento", "error");
          return;
        }
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar("Se ha subido el documento con éxito", "success");
        callback();
      } catch (e) {
        console.log(e)
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar(
          "Algo falló al subir el documento ",
          "error",
        );
        console.error(e);
      }
    },
    [toggleLoader, pageName, sencenet, documentType, showSnackbar],
  );

  const manuallyUploadAuthFile = useCallback(
    async (
      file: File,
      documentParticipantInfo: DocumentParticipantInfo | null,
      callback: () => void,
    ) => {
      if (!documentParticipantInfo) return;
      toggleLoader(true, `${pageName} Uploading file`);
      const { minorAuthorizationId } = documentParticipantInfo;
      // Send file
      try {
        const wasUploaded = await uploadManualDocument(
          {
            documentId: minorAuthorizationId,
            file,
          },
          {
            sencenet,
            documentType,
          },
          documentType,
        );
        if (!wasUploaded) {
          toggleLoader(false, `${pageName} Uploading file`);
          showSnackbar("Algo falló al subir el documento", "error");
          return;
        }
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar("Se ha subido el documento con éxito", "success");
        callback();
      } catch (e) {
        console.log(e)
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar(
          "Algo falló al subir el documento ",
          "error",
        );
        console.error(e);
      }
    },
    [toggleLoader, pageName, sencenet, documentType, showSnackbar],
  );

  const manuallyUploadVulnerabilityFile = useCallback(
    async (
      file: File,
      documentParticipantInfo: DocumentParticipantInfo | null,
      callback: () => void,
    ) => {
      if (!documentParticipantInfo) return;
      toggleLoader(true, `${pageName} Uploading file`);
      const { vulnerabilityId } = documentParticipantInfo;
      // Send file
      try {
        const wasUploaded = await uploadManualDocument(
          {
            documentId: vulnerabilityId,
            file,
          },
          {
            sencenet,
            documentType,
          },
          documentType,
        );
        if (!wasUploaded) {
          toggleLoader(false, `${pageName} Uploading file`);
          showSnackbar("Algo falló al subir el documento", "error");
          return;
        }
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar("Se ha subido el documento con éxito", "success");
        callback();
      } catch (e) {
        console.log(e)
        toggleLoader(false, `${pageName} Uploading file`);
        showSnackbar(
          "Algo falló al subir el documento ",
          "error",
        );
        console.error(e);
      }

    },
    [toggleLoader, pageName, sencenet, documentType, showSnackbar],
  );


  const onForceDocumentValidationFinish = useCallback(
    async (id: number, emailData: EmailForm) => {
      toggleLoader(true, `${pageName} Forcing document validation`);
      const forcedValidationSuccesful = await forceDocumentValidation(
        {
          documentId: id,
          comment: emailData?.message,
        },
        { sencenet, documentType },
        documentType,
      );
      toggleLoader(false, `${pageName} Forcing document validation`);
      if (!forcedValidationSuccesful) {
        showSnackbar("Algo falló al realizar la validación forzada", "error");
        return;
      }
      showSnackbar("Validación forzada realizada exitosamente", "success");
    },
    [documentType, pageName, sencenet, showSnackbar, toggleLoader],
  );

  // Passed as prop to be used in ValidationDialog (document replacement)
  const openUploadDialogCallback = useCallback(
    (docParticipantData: DocumentParticipantInfo) => () => {
      openUploadDialog({
        common: {
          onFinish: (file, callback) =>
            manuallyUploadFile(file, docParticipantData, callback),
          onClose: onCloseUploadDialog,
          title: getUploadDialogTitle(docParticipantData, documentType),
        },
        courseData: dialogInfoData,
        documentExists: !!docParticipantData.document.url || !!docParticipantData.documentVulnerability.id || !!docParticipantData.documentMinorAuth.id,
        stillOpenAt: route,
      });
      closeValidationDialog();
    },
    [
      closeValidationDialog,
      dialogInfoData,
      documentType,
      manuallyUploadFile,
      onCloseUploadDialog,
      openUploadDialog,
      route,
    ],
  );

  const openAuthUploadDialogCallback = useCallback(
    (docParticipantData: DocumentParticipantInfo) => () => {
      openUploadDialog({
        common: {
          onFinish: (file, callback) =>
            manuallyUploadAuthFile(file, docParticipantData, callback),
          onClose: onCloseUploadDialog,
          title: getUploadDialogTitle(docParticipantData, documentType),
        },
        courseData: dialogInfoData,
        documentExists: !!docParticipantData.document.url || !!docParticipantData.documentVulnerability.id || !!docParticipantData.documentMinorAuth.id,
        stillOpenAt: route,
      });
      closeValidationDialog();
    },
    [
      closeValidationDialog,
      dialogInfoData,
      documentType,
      manuallyUploadAuthFile,
      onCloseUploadDialog,
      openUploadDialog,
      route,
    ],
  );

  const openVulnerabilityUploadDialogCallback = useCallback(
    (docParticipantData: DocumentParticipantInfo) => () => {
      openUploadDialog({
        common: {
          onFinish: (file, callback) =>
            manuallyUploadVulnerabilityFile(file, docParticipantData, callback),
          onClose: onCloseUploadDialog,
          title: getUploadDialogTitle(docParticipantData, documentType),
        },
        courseData: dialogInfoData,
        documentExists: !!docParticipantData.document.url || !!docParticipantData.documentVulnerability.id || !!docParticipantData.documentMinorAuth.id,
        stillOpenAt: route,
      });
      closeValidationDialog();
    },
    [
      closeValidationDialog,
      dialogInfoData,
      documentType,
      manuallyUploadVulnerabilityFile,
      onCloseUploadDialog,
      openUploadDialog,
      route,
    ],
  );

  const onClickFile = useCallback(
    (id: number) => {
      if (!course) return;
      const docParticipantData = course.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      console.log(docParticipantData);
      if (!docParticipantData) return;
      const {
        participant: { name, rut },
        forcedValidationComment,
      } = docParticipantData;
      const {
        canValidate,
        canManualUpload,
        canView,
        canForceValidation,
        canViewForcedValidation,
        canReplace,
      } = getDocumentActions(docParticipantData.document);

      if (canManualUpload && docParticipantData.document.url === null) {
        openUploadDialog({
          common: {
            onFinish: (file, callback) =>
              file && manuallyUploadFile(file, docParticipantData, callback),
            onClose: onCloseUploadDialog,
            title: getUploadDialogTitle(docParticipantData, documentType),
          },
          courseData: dialogInfoData,
          stillOpenAt: route,
          documentExists: !!docParticipantData.document.url,
        });
      } else if (canValidate || canView) {
        openValidationDialog({
          common: {
            onClose: onCloseValidationDialog,
            title: getValidationDialogTitle(
              docParticipantData,
              course,
              documentType,
            ),
          },
          course,
          headerCourseData: dialogInfoData,
          participant: docParticipantData?.participant,
          sencenet,
          stillOpenAt: route,
          document: docParticipantData.document,
          actionable: canValidate || canReplace,
          openUploadDialog: openUploadDialogCallback(docParticipantData),
        });
      } else if (canForceValidation) {
        openMessageDialog({
          title: `Validación forzada ${DocumentShortNames[documentType]} · RUT ${rut} · ${name}`,
          onlyCommentMode: true,
          stillOpenAt: route,
          onFinish: (emailData: EmailForm) =>
            onForceDocumentValidationFinish(id, emailData),
        });
      } else if (canViewForcedValidation) {
        openInformationDialog({
          title: `${DocumentShortNames[documentType]} validada forzadamente · RUT ${rut} · ${name}`,
          text:
            forcedValidationComment ??
            "No hay comentarios relacionados a la validación forzada",
          stillOpenAt: route,
        });
      }
    },
    [
      course,
      getDocumentActions,
      openUploadDialog,
      onCloseUploadDialog,
      documentType,
      dialogInfoData,
      manuallyUploadFile,
      openValidationDialog,
      onCloseValidationDialog,
      sencenet,
      route,
      openUploadDialogCallback,
      openMessageDialog,
      onForceDocumentValidationFinish,
      openInformationDialog,
    ],
  );

  const onClickAuthFile = useCallback(
    (id: number) => {
      if (!course) return;
      const docParticipantData = course.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      if (!docParticipantData) return;
      if(docParticipantData.minorAuthorizationStatus === null ) return;
      const {
        participant: { name, rut },
        forcedValidationComment,
      } = docParticipantData;
      const {
        canViewMinorAuthorization,
        canManualUploadMinorAuthorization,
        canValidateMinorAuthorization,
        canForceValidation,
        canViewForcedValidation,
        canReplace
      } = getSpecialDocumentActions(docParticipantData);
      const authType = DocumentType.APME;

      if (canManualUploadMinorAuthorization && docParticipantData.documentMinorAuth.url === null) {
        openUploadDialog({
          common: {
            onFinish: (file, callback) => 
              file && manuallyUploadAuthFile(file, docParticipantData, callback),
            onClose: onCloseUploadDialog,
            title: getUploadDialogTitle(docParticipantData, authType),
          },
          courseData: dialogInfoData,
          stillOpenAt: route,
          documentExists: !!docParticipantData.documentMinorAuth.url,
        });
        } else if (canValidateMinorAuthorization || canViewMinorAuthorization) {
        openValidationDialog({
          common: {
            onClose: onCloseValidationDialog,
            title: getValidationDialogMinorAuthTitle(
              docParticipantData,
              course,
              authType,
            ),
          },
          course,
          headerCourseData: dialogInfoData,
          participant: docParticipantData?.participant,
          sencenet,
          stillOpenAt: route,
          document: docParticipantData.documentMinorAuth,
          actionable: (canValidateMinorAuthorization || canReplace) && !(docParticipantData.documentMinorAuth.status === DocumentStatus.VALIDATED),
          openUploadDialog: openAuthUploadDialogCallback(docParticipantData),
        });
      } else if (canForceValidation) {
        openMessageDialog({
          title: `Validación forzada ${DocumentShortNames[authType]} · RUT ${rut} · ${name}`,
          onlyCommentMode: true,
          stillOpenAt: route,
          onFinish: (emailData: EmailForm) =>
            onForceDocumentValidationFinish(id, emailData),
        });
      } else if (canViewForcedValidation) {
        openInformationDialog({
          title: `${DocumentShortNames[authType]} validada forzadamente · RUT ${rut} · ${name}`,
          text:
            forcedValidationComment ??
            "No hay comentarios relacionados a la validación forzada",
          stillOpenAt: route,
        });
      }
    },
    [
      course,
      getSpecialDocumentActions,
      openUploadDialog,
      onCloseUploadDialog,
      dialogInfoData,
      manuallyUploadAuthFile,
      openValidationDialog,
      onCloseValidationDialog,
      sencenet,
      route,
      openAuthUploadDialogCallback,
      openMessageDialog,
      onForceDocumentValidationFinish,
      openInformationDialog,
    ],
  );

  const onClickVulnerabilityFile = useCallback(
    (id: number) => {
      if (!course) return;
      const docParticipantData = course.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      console.log(docParticipantData);
      if (!docParticipantData) return;
      if(docParticipantData.vulnerabilityStatus === null ) return;
      const {
        participant: { name, rut },
        forcedValidationComment,
      } = docParticipantData;
      const {
        canManualUploadVulnerability,
        canViewVulnerability,
        canValidateVulnerability,
        canForceValidation,
        canViewForcedValidation,
        canReplace
      } = getSpecialDocumentActions(docParticipantData);
      const vulnerabilityType = DocumentType.VULNERABILITY;

      if (canManualUploadVulnerability && docParticipantData.documentVulnerability.url === null) {
        openUploadDialog({
          common: {
            onFinish: (file, callback) =>
              file && manuallyUploadVulnerabilityFile(file, docParticipantData, callback),
            onClose: onCloseUploadDialog,
            title: getUploadDialogTitle(docParticipantData, vulnerabilityType),
          },
          courseData: dialogInfoData,
          stillOpenAt: route,
          documentExists: !!docParticipantData.documentVulnerability.url,
        });
      } else if (canValidateVulnerability || canViewVulnerability) {
        openValidationDialog({
          common: {
            onClose: onCloseValidationDialog,
            title: getValidationDialogVulnerabilityTitle(
              docParticipantData,
              course,
              vulnerabilityType,
            ),
          },
          course,
          headerCourseData: dialogInfoData,
          participant: docParticipantData?.participant,
          sencenet,
          stillOpenAt: route,
          document: docParticipantData.documentVulnerability,
          actionable: (canValidateVulnerability || canReplace) && !(docParticipantData.documentVulnerability.status === DocumentStatus.VALIDATED),
          openUploadDialog: openVulnerabilityUploadDialogCallback(docParticipantData),
        });
      } else if (canForceValidation) {
        openMessageDialog({
          title: `Validación forzada ${DocumentShortNames[vulnerabilityType]} · RUT ${rut} · ${name}`,
          onlyCommentMode: true,
          stillOpenAt: route,
          onFinish: (emailData: EmailForm) =>
            onForceDocumentValidationFinish(id, emailData),
        });
      } else if (canViewForcedValidation) {
        openInformationDialog({
          title: `${DocumentShortNames[vulnerabilityType]} validada forzadamente · RUT ${rut} · ${name}`,
          text:
            forcedValidationComment ??
            "No hay comentarios relacionados a la validación forzada",
          stillOpenAt: route,
        });
      }
    },
    [
      course,
      getSpecialDocumentActions,
      openUploadDialog,
      onCloseUploadDialog,
      dialogInfoData,
      manuallyUploadVulnerabilityFile,
      openValidationDialog,
      onCloseValidationDialog,
      sencenet,
      route,
      openVulnerabilityUploadDialogCallback,
      openMessageDialog,
      onForceDocumentValidationFinish,
      openInformationDialog,
    ],
  );
















  const getFileButtonIconData = useCallback(
    (id: number): CellIconData | null => {
      const docParticipantData = course?.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      if (!docParticipantData) return null;
      if (!canInteractDocument(docParticipantData.document)) return null;
      const { status } = docParticipantData.document;
      const availableDocActions = getDocumentActions(
        docParticipantData.document,
      );
      return getDocumentIconButtonData(status, availableDocActions);
    },
    [canInteractDocument, course?.documentParticipantInfo, getDocumentActions],
  );

  const getParticipantAvailableFileIds = useCallback(
    (docParticipantData: DocumentParticipantInfo) => ({
      documentId: docParticipantData.document?.url
        ? docParticipantData.document.id
        : undefined,
      identityFileId: docParticipantData.participant?.identityFile?.url
        ? docParticipantData.participant.identityFile.id
        : undefined,
      minorAuthDocumentId: docParticipantData.documentMinorAuth?.url
        ? docParticipantData.documentMinorAuth.id
        : undefined,
      vulnerabilityDocumentId: docParticipantData.documentVulnerability?.url
        ? docParticipantData.documentVulnerability.id
        : undefined,
    }),
    [],
  );

  const getDownloadParticipantDocsIconData = useCallback(
    (id: number): CellIconData | null => {
      const docParticipantData = course?.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      if (!docParticipantData) return null;
      const fileIds = getParticipantAvailableFileIds(docParticipantData);
      const hasAnyFile = Object.values(fileIds).some(Boolean);
      return {
        icon: <FileDownloadOutlined />,
        tooltipLabel: hasAnyFile
          ? "Descargar documento participante"
          : "Sin documentos disponibles",
        disabled: !hasAnyFile,
      };
    },
    [course?.documentParticipantInfo, getParticipantAvailableFileIds],
  );

  const onDownloadParticipantDocuments = useCallback(
    async (id: number) => {
      const docParticipantData = course?.documentParticipantInfo.find(
        (p) => p.id === id,
      );
      if (!docParticipantData) return;
      const fileIds = getParticipantAvailableFileIds(docParticipantData);
      if (!Object.values(fileIds).some(Boolean)) return;
      const { name, rut } = docParticipantData.participant;
      toggleLoader(true, `${pageName} Downloading participant documents`);
      try {
        const base64 = await getParticipantMergedFileBase64(fileIds);
        if (!base64) throw new Error("Empty merged file");
        downloadBase64File(
          base64,
          buildParticipantFileName(sencenet, name, rut),
        );
      } catch (e) {
        console.log(e);
        showSnackbar(
          "Algo falló al descargar los documentos del participante",
          "error",
        );
      } finally {
        toggleLoader(false, `${pageName} Downloading participant documents`);
      }
    },
    [
      course?.documentParticipantInfo,
      getParticipantAvailableFileIds,
      toggleLoader,
      pageName,
      sencenet,
      showSnackbar,
    ],
  );

  const enrichedColumnDefs: TableColumnData[] = useMemo(() => {
    // Filter columns by permissions
    const colDefs = columnDefs.filter(
      (colDef) => !colDef.permissions || isAllowedTo(colDef.permissions, false),
    );

    // Inject styles to icon button cols
    colDefs
      .filter((col) => col.cellType === TableCellType.ICON_BUTTON)
      .forEach((col) => {
        col.style = styles.tableIcons;
      });
    // Inject callbacks
    const reminderCol = colDefs.find((col) => col.attr === "sendReminder");
    if (reminderCol) {
      reminderCol.onClick = onReminderClick;
      reminderCol.getIconData = getReminderButtonIconData;
    }

    const fileCol = colDefs.find((col) => col.attr === "document");
    if (fileCol) {
      fileCol.onClick = onClickFile;
      fileCol.getIconData = getFileButtonIconData;
    }

    const statusCol = colDefs.find((col) => col.attr === "mainSignerStatus");
    if (statusCol) statusCol.onClick = onClickFile;


    const authCol = colDefs.find((col) => col.attr === "minorAuthorizationStatus");
    if (authCol) authCol.onClick = onClickAuthFile;

    const vulnerabilityCol = colDefs.find((col) => col.attr === "vulnerabilityStatus");
    if (vulnerabilityCol ) vulnerabilityCol.onClick = onClickVulnerabilityFile;

    const downloadCol = colDefs.find(
      (col) => col.attr === "downloadParticipantDocuments",
    );
    if (downloadCol) {
      downloadCol.onClick = onDownloadParticipantDocuments;
      downloadCol.getIconData = getDownloadParticipantDocsIconData;
    }

    return colDefs;
  }, [
    columnDefs,
    onClickFile,
    onClickAuthFile,
    onClickVulnerabilityFile,
    isAllowedTo,
    styles.tableIcons,
    onReminderClick,
    getReminderButtonIconData,
    getFileButtonIconData,
    onDownloadParticipantDocuments,
    getDownloadParticipantDocsIconData,
  ]);

  /* EFFECTS */
  useEffect(() => {
    setParticipantsTableRawData(participantsData);
  }, [participantsData, setParticipantsTableRawData]);

  useEffect(() => {
    toggleLoader(loading, `${pageName} Loading course details data`);
  }, [loading, pageName, toggleLoader]);
  return (
    <>
      {course && (
        <AccordionInfo
          course={course}
          documentType={documentType}
          route={route}
        />
      )}
      <Box sx={styles.filterDiv}>
        <Stack sx={styles.stack} direction="row" spacing={2}>
          <Typography variant={"h6"}>
            {DocumentShortNames[documentType]}
          </Typography>
          <Divider
            sx={styles.divider}
            variant="middle"
            flexItem
            orientation="vertical"
          />
          <Group sx={styles.participantsCount} />
          <Typography sx={styles.participantsCount} variant={"h6"}>{`${
            course?.documentParticipantInfo?.length ?? 0
          } participantes activos`}</Typography>
        </Stack>
        <Stack sx={styles.stack} direction="row" spacing={2}>
          <Restricted
            to={[
              Permission.CAN_SEND_PARTICIPANT_MAIL,
              Permission.CAN_SEND_RESPONSABLE_MAIL,
            ]}
            every={false}
          >
            <CustomButton
              startIcon={<MailOutline />}
              variant="outlined"
              onClick={onOpenMassiveReminderDialog}
              disabled={
                disableReminderButtonsByCompanyStatus ||
                disableMassReminderButton
              }
            >
              Recordatorio masivo
            </CustomButton>
          </Restricted>
          <DownloadButton
            data={parsedExportableData}
            headers={csvHeaders}
            filename={`lista-${DocumentShortNames[documentType]}-SN_${course?.sencenet}.csv`}
          />
          <SearchBar
            onChange={searchBarOnChange}
            value={searchText}
            InputProps={{
              endAdornment: searchText !== "" && (
                <IconButton size="small" onClick={onClearIconClick}>
                  <Clear />
                </IconButton>
              ),
            }}
            placeholder={"Buscar por nombre o rut del participante"}
          />
        </Stack>
      </Box>
      {participantsTableRawData && (
        <CustomTable
          rawData={participantsTableRawData}
          columnDefs={enrichedColumnDefs}
          disableSorting={searchText.length > 2}
        />
      )}
      <MassiveReminderDialog
        participants={pendingManagementParticipants ?? []}
        open={massiveReminderDialogOpen}
        onClose={onCloseMassiveReminderDialog}
        onFinish={sendMassiveReminder}
        toResponsable={massiveReminderToResponsable}
      />
    </>
  );
};

const generateStyles: CSSGenerator = (theme: Theme) => ({
  filterDiv: {
    display: "flex",
    justifyContent: "space-between",
    margin: "32px 0",
    alignItems: "center",
  },
  stack: {
    alignItems: "center",
    height: "fit-content",
  },
  button: {
    borderRadius: 18,
    height: 36,
  },
  participantsCount: {
    color: "rgba(0, 0, 0, 0.54)",
  },
  divider: {
    marginTop: 12,
    marginBottom: 12,
  },
  tableIcons: {
    color: theme.palette.common.black,
  },
});
